import { Card, CardBody, CardHeader, Select, SelectItem, Switch } from "@nextui-org/react"
import { DoxType, ExtractorType } from "../../types/types"
import { useEffect, useState, useMemo } from "react"
import { useFirestore, useFirestoreDocData, useAuth, useFunctions } from "reactfire"
import { doc } from "firebase/firestore"
import { formatBytes } from "../../utils/string"
import { LineChart } from "@mui/x-charts"
import { httpsCallable } from "firebase/functions"

export interface MonitorProps {
  dox: DoxType
}

interface InvocationData {
  date: string;
  apiKeyName: string;
  count: number;
}

interface TestRunData {
  date: string;
  count: number;
}

interface InvocationsResponse {
  invocations: InvocationData[];
  testRuns: TestRunData[];
  dateRange: {
    start: string;
    end: string;
  };
}

export const MonitorDashboard: React.FC<MonitorProps> = ({ dox }) => {

  const [usageStats, setUsageStats] = useState<any>();
  const firestore = useFirestore();
  const auth = useAuth();
  const functions = useFunctions();
  const { data: usage } = useFirestoreDocData(doc(firestore, 'usage/' + dox.id));

  const [chartData, setChartData] = useState<any[]>([]);
  const [invocationsData, setInvocationsData] = useState<InvocationsResponse | null>(null);
  const [loadingChart, setLoadingChart] = useState(true);
  const [timeRange, setTimeRange] = useState<number>(7);
  const [granularity, setGranularity] = useState<string>('day');
  const [showOnlyApiKeys, setShowOnlyApiKeys] = useState<boolean>(false);

  useEffect(() => {
    if (usage) {
      setUsageStats(usage);
      setChartData([
        {
          name: 'Invocations',
          data: [usage['invocations']]
        },
        {
          name: 'Errors',
          data: [usage['errors']]
        }
      ])
    }
  }, [usage]);

  useEffect(() => {
    const fetchInvocationsData = async () => {
      if (!auth.currentUser || !dox.id) {
        setLoadingChart(false);
        return;
      }

      try {
        setLoadingChart(true);

        // Use Firebase callable function pattern
        const fnName = process.env.NODE_ENV === 'development'
          ? `api//monitor/${dox.id}/invocations`
          : `api/monitor/${dox.id}/invocations`;

        const callableFunction = httpsCallable(functions, fnName);
        const response = await callableFunction({
          days: timeRange,
          granularity: granularity,
        });

        // Firebase callable functions wrap the response in data
        const responseData = response as any;
        console.log('Invocations data received:', responseData);

        if (responseData && responseData.data) {
          setInvocationsData(responseData.data);
        } else {
          console.warn('No data in response:', responseData);
          setInvocationsData(null);
        }
      } catch (error: any) {
        console.error('Error fetching invocations data:', error);
        console.error('Error details:', error.message, error.code, error.details);
        setInvocationsData(null);
      } finally {
        setLoadingChart(false);
      }
    };

    fetchInvocationsData();
  }, [auth.currentUser, dox.id, timeRange, granularity]);

  // Transform data for chart
  const chartSeriesData = useMemo(() => {
    if (!invocationsData) return { series: [], xLabels: [], apiKeySeries: [], otherSeries: [] };

    // Get all unique dates
    const allDates = new Set<string>();
    invocationsData.invocations.forEach(inv => allDates.add(inv.date));
    if (!showOnlyApiKeys) {
      invocationsData.testRuns.forEach(tr => allDates.add(tr.date));
    }

    // Fill in date range
    if (invocationsData.dateRange.start && invocationsData.dateRange.end) {
      const start = new Date(invocationsData.dateRange.start);
      const end = new Date(invocationsData.dateRange.end);
      const current = new Date(start);
      while (current <= end) {
        allDates.add(current.toISOString().split('T')[0]);
        current.setDate(current.getDate() + 1);
      }
    }

    const sortedDates = Array.from(allDates).sort();

    // Separate API keys from Manual/Test
    const apiKeyNames = new Set<string>();
    const manualTestKey = 'Manual/Test';
    const hasManualTest = new Set<string>();

    invocationsData.invocations.forEach(inv => {
      if (inv.apiKeyName !== manualTestKey) {
        apiKeyNames.add(inv.apiKeyName);
      } else {
        hasManualTest.add(inv.apiKeyName);
      }
    });

    // Build data structure: date -> { apiKeyName: count, ... }
    const dataByDate: Record<string, Record<string, number>> = {};
    sortedDates.forEach(date => {
      dataByDate[date] = {};
      Array.from(apiKeyNames).forEach(key => {
        dataByDate[date][key] = 0;
      });
      if (!showOnlyApiKeys) {
        if (hasManualTest.size > 0) {
          dataByDate[date][manualTestKey] = 0;
        }
        dataByDate[date]['Test Runs'] = 0;
      }
    });

    // Populate invocations
    invocationsData.invocations.forEach(inv => {
      // Filter: if showOnlyApiKeys is true, skip Manual/Test
      if (showOnlyApiKeys && inv.apiKeyName === manualTestKey) {
        return;
      }
      if (dataByDate[inv.date] && dataByDate[inv.date][inv.apiKeyName] !== undefined) {
        dataByDate[inv.date][inv.apiKeyName] += inv.count;
      }
    });

    // Populate test runs (only if filter is off)
    if (!showOnlyApiKeys) {
      invocationsData.testRuns.forEach(tr => {
        if (dataByDate[tr.date]) {
          dataByDate[tr.date]['Test Runs'] += tr.count;
        }
      });
    }

    // Format dates for x-axis (e.g., "Jan 1")
    const dateFormatter = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric'
    });

    const xLabels = sortedDates.map(date => {
      const d = new Date(date + 'T00:00:00');
      return dateFormatter.format(d);
    });

    // Create series - separate API keys from others
    const apiKeySeries: Array<{ data: number[], label: string, type: 'line' }> = [];
    const otherSeries: Array<{ data: number[], label: string, type: 'line' }> = [];

    // Add series for each API key (sorted for consistency)
    const sortedApiKeyNames = Array.from(apiKeyNames).sort();
    sortedApiKeyNames.forEach(keyName => {
      const data = sortedDates.map(date => dataByDate[date][keyName] || 0);
      apiKeySeries.push({
        data,
        label: keyName,
        type: 'line' as const,
      });
    });

    // Add Manual/Test series (if not filtered and exists)
    if (!showOnlyApiKeys && hasManualTest.size > 0) {
      const manualTestData = sortedDates.map(date => dataByDate[date][manualTestKey] || 0);
      otherSeries.push({
        data: manualTestData,
        label: manualTestKey,
        type: 'line' as const,
      });
    }

    // Add test runs series (if not filtered)
    if (!showOnlyApiKeys) {
      const testRunsData = sortedDates.map(date => dataByDate[date]['Test Runs'] || 0);
      otherSeries.push({
        data: testRunsData,
        label: 'Test Runs',
        type: 'line' as const,
      });
    }

    // Combine all series for the chart - API keys first, then others
    const allSeries = [...apiKeySeries, ...otherSeries];

    return { series: allSeries, xLabels, apiKeySeries, otherSeries };
  }, [invocationsData, showOnlyApiKeys]);

  const _dataCard = (title: string, value: string, bgcolor: string = "bg-primary-50") => (
    <Card className="bg-primary-50 xl:max-w-md rounded-xl shadow-md px-3 w-full">
      <CardHeader>
        <div className="flex gap-2.5 justify-center w-full">
          <span className="uppercase font-bold text-tiny">{title}</span>
        </div>
      </CardHeader>
      <CardBody className="overflow-hidden">
        <div className="flex gap-2.5 py-2 justify-center w-full">
          <span className=" text-2xl font-semibold">{value}</span>
        </div>
      </CardBody>
    </Card>
  )

  return (
    <div className="flex w-full min-h-[65vh]">
      {usage ? (
        <div className="grid grid-cols-3">
          <div className="flex flex-col gap-2 mt-2 mx-4">
            <h3 className="text-lg">Usage Metrics</h3>
            <div className="flex flex-col gap-2 mt-2 mx-4">
              <div className="grid md:grid-cols-2 grid-cols-1 2xl:grid-cols-2 gap-5  justify-center w-full">
                {_dataCard("Invocations", usage['invocations'])}
                {_dataCard("Errors", usage['errors'])}

              </div>
            </div>
            <h3 className="text-lg ">Pages</h3>
            <div className="flex flex-col gap-2 mt-2 mx-4">
              <div className="grid md:grid-cols-2 grid-cols-1 2xl:grid-cols-2 gap-5  justify-center w-full">
                {_dataCard("Daily Pages", usage['dailyPages'])}
                {_dataCard("Daily Scanned Pages", usage['dailyScanned'])}
                {_dataCard("Total Pages", usage['totalPages'])}
                {_dataCard("Total Scanned Pages", usage['totalScanned'])}
              </div>
            </div>
            <h3 className="text-lg">Data Ingress/Egress</h3>
            <div className="flex flex-col gap-2 mt-2 mx-4">
              <div className="grid md:grid-cols-2 grid-cols-1 2xl:grid-cols-2 gap-5  justify-center w-full">
                {_dataCard("Total Ingress", formatBytes(usage['ingress']))}
                {_dataCard("Total Egress", formatBytes(usage['egress']))}
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 mt-2 mx-4 col-span-2 ">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg">Invocations by day</h3>
              <div className="flex gap-4 items-center">
                <Switch
                  isSelected={showOnlyApiKeys}
                  onValueChange={setShowOnlyApiKeys}
                  size="sm"
                >
                  API Keys Only
                </Switch>
                <Select
                  label="Time Range"
                  selectedKeys={[timeRange.toString()]}
                  onSelectionChange={(keys) => {
                    const selected = Array.from(keys)[0] as string;
                    setTimeRange(parseInt(selected));
                  }}
                  className="w-40"
                  size="sm"
                >
                  <SelectItem key="7" value="7">Last 7 days</SelectItem>
                  <SelectItem key="30" value="30">Last 30 days</SelectItem>
                </Select>
                <Select
                  label="Granularity"
                  selectedKeys={[granularity]}
                  onSelectionChange={(keys) => {
                    const selected = Array.from(keys)[0] as string;
                    setGranularity(selected);
                  }}
                  className="w-32"
                  size="sm"
                >
                  <SelectItem key="day" value="day">By Day</SelectItem>
                  <SelectItem key="hour" value="hour">By Hour</SelectItem>
                </Select>
              </div>
            </div>
            {loadingChart ? (
              <div className="flex items-center justify-center h-[300px]">
                <span className="text-lg">Loading chart data...</span>
              </div>
            ) : chartSeriesData.series.length > 0 ? (
              <div>
                <LineChart
                  width={800}
                  height={300}
                  series={chartSeriesData.series}
                  xAxis={[{ scaleType: 'point', data: chartSeriesData.xLabels }]}
                  slotProps={{
                    legend: {
                      direction: 'row',
                      position: { vertical: 'bottom', horizontal: 'middle' },
                      padding: { top: 20 },
                    },
                  }}
                  margin={{ top: 10, bottom: 60, left: 50, right: 50 }}
                />
                {chartSeriesData.apiKeySeries.length > 0 && (
                  <div className="mt-2 text-xs text-gray-600">
                    <strong>API Keys:</strong> {chartSeriesData.apiKeySeries.map(s => s.label).join(', ')}
                    {chartSeriesData.otherSeries.length > 0 && (
                      <>
                        {' | '}
                        <strong>Other:</strong> {chartSeriesData.otherSeries.map(s => s.label).join(', ')}
                      </>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-[300px]">
                <span className="text-lg text-gray-500">No data available for the selected time range</span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2 mt-2 mx-4">
          <h3 className="text-lg font-semibold">Loading...</h3>
        </div>
      )
      }
    </div>
  )
}